import { Box, HStack, Text } from '@chakra-ui/react';
import { Clock } from 'iconoir-react';
import { SiMeta } from '@icons-pack/react-simple-icons';
import { useTranslation } from 'react-i18next';
import { Alert } from '@/components/ui/alert';
import { MentionEmptyCard } from '../MentionEmptyCard';

/**
 * A quiet info notice that delivery figures are still settling during the
 * provider's attribution window. Routed through the shared `Alert` (info) so
 * the treatment comes from one recipe, like the readiness notice and setup
 * error, instead of raw color steps. Nothing is wrong; the numbers are simply
 * not final yet.
 */
export function ConsolidatingNotice() {
  const { t } = useTranslation();

  return (
    <Alert
      status="info"
      variant="surface"
      icon={<Clock width={16} height={16} />}
      data-testid="boost-insights-consolidating"
    >
      {t('mentions.boost.insights.consolidating')}
    </Alert>
  );
}

/**
 * Clean empty state when the boost has produced no provider data yet. Shares the
 * mention pane's one empty card with the organic timeline, so "nothing to show
 * yet" reads identically wherever it appears — no zeroed metric grid that would
 * pass for real outcomes.
 */
export function NoDataState() {
  const { t } = useTranslation();

  return (
    <MentionEmptyCard
      title={t('mentions.boost.insights.noData.title')}
      description={t('mentions.boost.insights.noData.body')}
      data-testid="boost-insights-no-data"
    />
  );
}

interface AttributionLabelProps {
  /** ISO timestamp of the last provider sync, or null when never synced. */
  lastSyncedLabel: string | null;
}

/**
 * Makes the provenance explicit: figures are Meta-modeled and never blended
 * with Fluenceur's own attribution. Optionally carries the "last updated"
 * timestamp so the marketer knows how fresh the read is.
 */
export function AttributionLabel({ lastSyncedLabel }: AttributionLabelProps) {
  const { t } = useTranslation();

  return (
    <HStack gap={1.5} align="center" data-testid="boost-insights-attribution">
      <Box flexShrink={0} lineHeight={0}>
        <SiMeta size={13} color="#0866FF" />
      </Box>
      <Text fontSize="xs" color="fg.muted">
        {t('mentions.boost.insights.attribution')}
        {lastSyncedLabel
          ? ` ${t('mentions.boost.insights.lastUpdated', { value: lastSyncedLabel })}`
          : ''}
      </Text>
    </HStack>
  );
}
